/**
 * Flow provider.
 *
 * Manages flow-resolver registration from components throughout the tree.
 * Uses Immer for reference stability to prevent unnecessary re-renders.
 */

import type { ReactNode } from 'react';
import { useImmer } from 'use-immer';

import { FlowContext, type FlowContextType } from '#plugin-chat/contexts/FlowContext.ts';
import type { FlowResolverEntry } from '#plugin-chat/lib/flow/types.ts';

/**
 * Provider that manages flow-resolver registration.
 *
 * Components register resolvers via `use(FlowContext).registerFlowResolver()`.
 * Resolvers are collected and sorted ascending by priority when accessed via
 * `getFlowResolvers()`. By default zero resolvers are registered, so an
 * un-extended tree drives a no-op engine.
 */
export function FlowProvider({ children }: { children: ReactNode }) {
  const [resolvers, setResolvers] = useImmer<FlowResolverEntry[]>([]);

  const contextValue: FlowContextType = {
    registerFlowResolver(entry) {
      setResolvers((draft) => {
        draft.push(entry);
      });

      return () => {
        setResolvers((draft) => {
          const index = draft.findIndex(
            (candidate) => candidate.priority === entry.priority && candidate.resolve === entry.resolve,
          );
          if (index !== -1) {
            draft.splice(index, 1);
          }
        });
      };
    },

    getFlowResolvers() {
      return resolvers.toSorted((a, b) => a.priority - b.priority);
    },
  };

  return <FlowContext value={contextValue}>{children}</FlowContext>;
}
